import { faLoader, faSort } from "@fortawesome/pro-light-svg-icons";
import { faSort as faSortFilled } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faChevronsLeft,
    faChevronsRight,
    faSatellite,
    faSwap,
} from "@fortawesome/sharp-light-svg-icons";
import { useQuery } from "@tanstack/react-query";
import { useDebounce } from "@uidotdev/usehooks";
import { atom, useAtom } from "jotai";
import { atomWithStorage } from "jotai/utils";
import { type ReactNode, useEffect, useMemo, useState } from "react";
import type {
    AdminEmissionsRecordsStatsListProviderWithSourceParameterInner,
    DataSourceEnum,
    EmissionRecordStatsResponse,
    EmissionRecordView,
    PaginatedEmissionRecordViewList,
} from "../../../../../apiClient/generated";
import {
    useEmissionRecordsApiClient,
    useInfrastructureApiClient,
} from "../../../../../hooks";
import { formatDate } from "../../../../../utils/dates";
import { EventStatusV2Display } from "../../../../EventsV2/EventStatusDisplay";
import { useDrawAndFilterControlsV2 } from "../../../hooks/areaFilter";
import { useMapData, useMapFilters } from "../../../hooks/mapDataAndFilters";
import { useMap } from "../../../hooks/mapState";
import { automaticPanAndZoomAtom, prevMapViewStateAtom } from "../../../state";
import { InsightsContainer } from "../../../ui/MapControlsV2";
import { insightsConfigAtom } from "../state";
import { BridgerEmissionInsights } from "./Bridger";
import { ThirdPartyEmissionInsights } from "./Emissions";
import { InfrastuctureInsightsRow } from "./Infrastructure";
import { NonDetectInsights } from "./NonDetects";
import { SettingsDropdownProvider } from "./ui";

const LoadingOverlay = () => (
    <div className="absolute h-full w-full text-ae-slate-600 bg-white bg-opacity-60 flex items-center justify-center">
        <FontAwesomeIcon icon={faLoader} className="h-4 w-4 animate-spin" />
    </div>
);

interface CountProps {
    label: string | ReactNode;
    value?: number;
    isLoading?: boolean;
}

const Count = ({ label, value, isLoading }: CountProps) => (
    <div className="flex flex-col px-2 relative">
        <span className="w-full text-center font-bold text-xl">
            {(value || 0).toFixed(0)}
        </span>
        <span className="w-full text-center text-xs">{label}</span>
        {isLoading && <LoadingOverlay />}
    </div>
);

const GeneralCounts = () => {
    const infrastructureApiClient = useInfrastructureApiClient();
    const emissionsApiClient = useEmissionRecordsApiClient();
    const { debounced } = useMap("mainMap");
    const { filterState } = useMapFilters("mainMap");
    const { selectedFilters, filterData, areaToFetch } =
        useDrawAndFilterControlsV2("mainMap");
    const { selectedContext } = useMapData("mainMap");
    const [showAvg, setShowAvg] = useState(true);

    const sitesCountQuery = useQuery({
        queryKey: ["siteCountForArea", areaToFetch, selectedFilters],
        queryFn: async () => {
            return await infrastructureApiClient.infrastructureSitesList({
                pageSize: 1,
                locationWithin: areaToFetch,
                locationPreset:
                    filterData && selectedFilters.length > 0
                        ? selectedFilters
                        : undefined,
            });
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled: !!debounced.areaOnScreen,
        staleTime: 500,
    });

    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore =
                filterState.emissions.endDateFilter !== ""
                    ? new Date(filterState.emissions.endDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.end) {
                dateBefore = new Date(selectedContext.dateRangeToShowData.end);
            }
            let dateAfter =
                filterState.emissions.startDateFilter !== ""
                    ? new Date(filterState.emissions.startDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.start) {
                dateAfter = new Date(selectedContext.dateRangeToShowData.start);
            }
            return { dateBefore, dateAfter };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            selectedContext.dateRangeToShowData,
        ]),
        400,
    );

    const emissionThirdPartyStats = useQuery({
        queryKey: [
            "emissionStatsForArea",
            "thirdParty",
            areaToFetch,
            filterState.emissions,
            debouncedDateRange,
            selectedFilters,
        ],
        queryFn: async (): Promise<EmissionRecordStatsResponse> => {
            const providerFilter =
                filterState.emissions.emissionGroupFiltersV2.thirdParty;
            if (
                !filterState.emissions.showEmissions ||
                providerFilter.length === 0
            ) {
                return {
                    _new: 0,
                    avgRate: 0,
                    complete: 0,
                    highestRate: 0,
                    inProgress: 0,
                    lastNDays: 0,
                    totalRate: 0,
                    total: 0,
                };
            }

            return await emissionsApiClient.emissionRecordsEmissionRecordStatsRetrieve(
                {
                    dataSource: ["THIRD_PARTY"],
                    providerWithSource:
                        providerFilter === "all"
                            ? undefined
                            : (JSON.stringify(providerFilter) as any),
                    detectionDateRangeAfter:
                        filterState.emissions.filterEmissionsBy ===
                        "detection_date"
                            ? debouncedDateRange.dateAfter
                            : undefined,
                    detectionDateRangeBefore:
                        filterState.emissions.filterEmissionsBy ===
                        "detection_date"
                            ? debouncedDateRange.dateBefore
                            : undefined,
                    createdAtAfter:
                        filterState.emissions.filterEmissionsBy === "match_date"
                            ? debouncedDateRange.dateAfter
                            : undefined,
                    createdAtBefore:
                        filterState.emissions.filterEmissionsBy === "match_date"
                            ? debouncedDateRange.dateBefore
                            : undefined,
                    excludeEventStatus: ["REJECTED"],
                    detectedRateMin: filterState.emissions.detectedRateMin
                        ? filterState.emissions.detectedRateMin * 1000
                        : undefined,
                    detectedRateMax: filterState.emissions.detectedRateMax
                        ? filterState.emissions.detectedRateMax * 1000
                        : undefined,
                    locationWithin: areaToFetch,
                    locationPreset:
                        filterData && selectedFilters.length > 0
                            ? selectedFilters
                            : undefined,
                },
            );
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled: !!debounced.areaOnScreen,
        staleTime: 500,
    });

    const emissionSelfReportedStats = useQuery({
        queryKey: [
            "emissionStatsForArea",
            "selfReported",
            areaToFetch,
            filterState.emissions,
            selectedFilters,
        ],
        queryFn: async (): Promise<EmissionRecordStatsResponse> => {
            const providerFilter =
                filterState.emissions.emissionGroupFiltersV2.selfReported;
            if (
                !filterState.emissions.showEmissions ||
                providerFilter.length === 0
            ) {
                return {
                    _new: 0,
                    avgRate: 0,
                    complete: 0,
                    highestRate: 0,
                    inProgress: 0,
                    lastNDays: 0,
                    total: 0,
                    totalRate: 0,
                };
            }

            return await emissionsApiClient.emissionRecordsEmissionRecordStatsRetrieve(
                {
                    dataSource: ["SELF_REPORTED"],
                    providerWithSource:
                        providerFilter === "all"
                            ? undefined
                            : (JSON.stringify(providerFilter) as any),
                    detectionDateRangeAfter:
                        filterState.emissions.filterEmissionsBy ===
                        "detection_date"
                            ? debouncedDateRange.dateAfter
                            : undefined,
                    detectionDateRangeBefore:
                        filterState.emissions.filterEmissionsBy ===
                        "detection_date"
                            ? debouncedDateRange.dateBefore
                            : undefined,
                    createdAtAfter:
                        filterState.emissions.filterEmissionsBy === "match_date"
                            ? debouncedDateRange.dateAfter
                            : undefined,
                    createdAtBefore:
                        filterState.emissions.filterEmissionsBy === "match_date"
                            ? debouncedDateRange.dateBefore
                            : undefined,
                    excludeEventStatus: ["REJECTED"],
                    detectedRateMin: filterState.emissions.detectedRateMin
                        ? filterState.emissions.detectedRateMin * 1000
                        : undefined,
                    detectedRateMax: filterState.emissions.detectedRateMax
                        ? filterState.emissions.detectedRateMax * 1000
                        : undefined,
                    locationWithin: areaToFetch,
                    locationPreset:
                        filterData && selectedFilters.length > 0
                            ? selectedFilters
                            : undefined,
                },
            );
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled: !!debounced.areaOnScreen,
        staleTime: 500,
    });

    // Combine results
    const combinedStats = useMemo(() => {
        const thirdParty = emissionThirdPartyStats.data;
        const selfReported = emissionSelfReportedStats.data;

        if (!thirdParty || !selfReported) {
            return null;
        }

        return {
            _new: thirdParty._new + selfReported._new,
            avgRate:
                (thirdParty.avgRate * thirdParty.total +
                    selfReported.avgRate * selfReported.total) /
                (2 * thirdParty.total + selfReported.total),
            complete: thirdParty.complete + selfReported.complete,
            highestRate: Math.max(
                thirdParty.highestRate,
                selfReported.highestRate,
            ),
            inProgress: thirdParty.inProgress + selfReported.inProgress,
            lastNDays: thirdParty.lastNDays + selfReported.lastNDays,
            total: thirdParty.total + selfReported.total,
            totalRate: thirdParty.totalRate + selfReported.totalRate,
        };
    }, [emissionThirdPartyStats.data, emissionSelfReportedStats.data]);

    return (
        <InsightsContainer>
            <div className="w-full flex items-center justify-between py-1">
                <Count
                    label="In progress"
                    value={combinedStats?.inProgress}
                    isLoading={
                        emissionThirdPartyStats.isFetching ||
                        emissionSelfReportedStats.isFetching
                    }
                />
                <Count
                    label="Total events"
                    value={combinedStats?.total}
                    isLoading={
                        emissionThirdPartyStats.isFetching ||
                        emissionSelfReportedStats.isFetching
                    }
                />
                <Count
                    label={
                        <button
                            onClick={() => setShowAvg((v) => !v)}
                            className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-neutral-100 border border-transparent hover:border-neutral-300 px-1 rounded transition-colors"
                        >
                            <span>
                                {showAvg ? "Avg." : "Total"} Rate (kg/h)
                            </span>
                            <FontAwesomeIcon icon={faSwap} className="h-2.5" />
                        </button>
                    }
                    value={
                        (showAvg
                            ? combinedStats?.avgRate
                            : combinedStats?.totalRate || 0) / 1000
                    }
                    isLoading={
                        emissionThirdPartyStats.isFetching ||
                        emissionSelfReportedStats.isFetching
                    }
                />
                <Count
                    label="Sites"
                    value={sitesCountQuery.data?.count}
                    isLoading={sitesCountQuery.isFetching}
                />
            </div>
        </InsightsContainer>
    );
};

const emissionTypeAtom = atomWithStorage<DataSourceEnum>(
    "emissionInsightsType",
    "THIRD_PARTY",
);
const emissionDateShownAtom = atomWithStorage<
    "detection_timestamp" | "match_timestamp"
>("emissionInsightsDateShown", "detection_timestamp");
const emissionTableStateAtom = atom({
    page: 1,
    ordering: {
        field: "data_point__detection_timestamp",
        order: "desc",
    },
});

const EmissionList = () => {
    const emissionsApiClient = useEmissionRecordsApiClient();

    // Map hooks and data
    const [, setPrevMapViewState] = useAtom(prevMapViewStateAtom);
    const { flyTo, debounced } = useMap("mainMap");
    const { filterState } = useMapFilters("mainMap");
    const { selectedFilters, filterData, areaToFetch } =
        useDrawAndFilterControlsV2("mainMap");
    const { selectedContext, setSelectedContext } = useMapData("mainMap");
    const [autoMove] = useAtom(automaticPanAndZoomAtom);

    // Table controls
    const [dateShown, setDateShown] = useAtom(emissionDateShownAtom);
    const [emissionType, setEmissionType] = useAtom(emissionTypeAtom);
    const [tableState, setTableState] = useAtom(emissionTableStateAtom);

    const debouncedDateRange = useDebounce(
        useMemo(() => {
            let dateBefore =
                filterState.emissions.endDateFilter !== ""
                    ? new Date(filterState.emissions.endDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.end) {
                dateBefore = new Date(selectedContext.dateRangeToShowData.end);
            }
            let dateAfter =
                filterState.emissions.startDateFilter !== ""
                    ? new Date(filterState.emissions.startDateFilter)
                    : undefined;
            if (selectedContext.dateRangeToShowData?.start) {
                dateAfter = new Date(selectedContext.dateRangeToShowData.start);
            }
            return { dateBefore, dateAfter };
        }, [
            filterState.emissions.startDateFilter,
            filterState.emissions.endDateFilter,
            selectedContext.dateRangeToShowData,
        ]),
        400,
    );

    const emissionsQuery = useQuery({
        queryKey: [
            "emissionsForArea",
            emissionType,
            areaToFetch,
            filterState.emissions,
            debouncedDateRange,
            tableState,
        ],
        queryFn: async (): Promise<PaginatedEmissionRecordViewList> => {
            let short = false;
            let providerWithSource: AdminEmissionsRecordsStatsListProviderWithSourceParameterInner[];
            if (emissionType === "THIRD_PARTY") {
                if (
                    filterState.emissions.emissionGroupFiltersV2.thirdParty ===
                    "all"
                ) {
                    providerWithSource = undefined;
                } else if (
                    filterState.emissions.emissionGroupFiltersV2.thirdParty
                        .length < 1
                ) {
                    short = true;
                } else {
                    providerWithSource =
                        filterState.emissions.emissionGroupFiltersV2.thirdParty;
                }
            } else if (emissionType === "SELF_REPORTED") {
                if (
                    filterState.emissions.emissionGroupFiltersV2
                        .selfReported === "all"
                ) {
                    providerWithSource = undefined;
                } else if (
                    filterState.emissions.emissionGroupFiltersV2.selfReported
                        .length < 1
                ) {
                    short = true;
                } else {
                    providerWithSource =
                        filterState.emissions.emissionGroupFiltersV2
                            .selfReported;
                }
            }

            if (!filterState.emissions.showEmissions || short) {
                return {
                    count: 0,
                    results: [],
                    numPages: 0,
                };
            }
            return await emissionsApiClient.emissionRecordsList({
                dataSource: [emissionType],
                providerWithSource: providerWithSource
                    ? (JSON.stringify(providerWithSource) as any)
                    : undefined,
                detectionDateRangeAfter:
                    filterState.emissions.filterEmissionsBy === "detection_date"
                        ? debouncedDateRange.dateAfter
                        : undefined,
                detectionDateRangeBefore:
                    filterState.emissions.filterEmissionsBy === "detection_date"
                        ? debouncedDateRange.dateBefore
                        : undefined,
                createdAtAfter:
                    filterState.emissions.filterEmissionsBy === "match_date"
                        ? debouncedDateRange.dateAfter
                        : undefined,
                createdAtBefore:
                    filterState.emissions.filterEmissionsBy === "match_date"
                        ? debouncedDateRange.dateBefore
                        : undefined,
                excludeEventStatus: ["REJECTED"],
                detectedRateMin: filterState.emissions.detectedRateMin
                    ? filterState.emissions.detectedRateMin * 1000
                    : undefined,
                detectedRateMax: filterState.emissions.detectedRateMax
                    ? filterState.emissions.detectedRateMax * 1000
                    : undefined,
                locationWithin: areaToFetch,
                locationPreset:
                    filterData && selectedFilters.length > 0
                        ? selectedFilters
                        : undefined,
                // Table control
                pageSize: 5,
                page: tableState.page,
                ordering: `${tableState.ordering.order === "desc" ? "-" : ""}${tableState.ordering.field}`,
            });
        },
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        enabled: !!debounced.areaOnScreen,
        staleTime: 500,
        retry: 1,
    });

    useEffect(() => {
        const handleError = async () => {
            const errorResponse = (emissionsQuery.error as any).response;
            const responseClone = errorResponse.clone();
            try {
                const response = await responseClone.json();
                if (response.detail === "Invalid page.") {
                    setTableState((ts) => ({
                        ...ts,
                        page: 1,
                    }));
                }
            } catch (error) {
                console.error("Error parsing response:", error);
            }
        };
        if (emissionsQuery.isError) {
            handleError();
        }
    }, [emissionsQuery, setTableState]);

    const renderEmissions = (emissions: EmissionRecordView[]) => {
        if (!emissions || emissions.length === 0) {
            return (
                <div className="w-full p-4 text-center text-gray-500">
                    No emissions data available
                </div>
            );
        }
        const goToEmission = (emission: EmissionRecordView) => {
            if (autoMove) {
                setPrevMapViewState(debounced.viewState);
                flyTo(
                    emission.dataPoint.location.coordinates[1],
                    emission.dataPoint.location.coordinates[0],
                    15,
                    true,
                );
            }
            setSelectedContext({
                emissionRecordId: emission.id,
                relatedPlume: emission.dataPoint.plumeImage,
            });
        };

        return (
            <table className="w-full text-sm border-collapse whitespace-nowrap">
                <thead>
                    <tr className="text-xs text-gray-500 whitespace-nowrap text-right border-b">
                        <th className="text-left font-normal py-0.5">#</th>
                        <th>
                            <div className="flex items-center justify-center">
                                <button
                                    type="button"
                                    className="flex items-center justify-center gap-1"
                                    onClick={() =>
                                        setTableState((ts) => {
                                            const newState = { ...ts };
                                            if (
                                                newState.ordering.field !==
                                                "data_point__detected_rate"
                                            ) {
                                                newState.ordering.field =
                                                    "data_point__detected_rate";
                                                newState.ordering.order =
                                                    "desc";
                                            } else {
                                                newState.ordering.order =
                                                    newState.ordering.order ===
                                                    "desc"
                                                        ? "asc"
                                                        : "desc";
                                            }
                                            return newState;
                                        })
                                    }
                                >
                                    Rate/Conc.
                                    <FontAwesomeIcon
                                        icon={
                                            tableState.ordering.field ===
                                            "data_point__detected_rate"
                                                ? faSortFilled
                                                : faSort
                                        }
                                        className={`
                                            cursor-pointer hover:text-black
                                            ${tableState.ordering.field === "data_point__detected_rate" ? "text-black" : "text-gray-400"}
                                        `}
                                        size="sm"
                                    />
                                </button>
                            </div>
                        </th>
                        <th className="py-0.5">
                            <div className="flex items-center justify-end">
                                <button
                                    type="button"
                                    className="flex items-center justify-center gap-1"
                                    onClick={() =>
                                        setTableState((ts) => {
                                            const newState = { ...ts };
                                            const field_name =
                                                dateShown ===
                                                "detection_timestamp"
                                                    ? "data_point__detection_timestamp"
                                                    : "created_at";
                                            if (
                                                newState.ordering.field !==
                                                field_name
                                            ) {
                                                newState.ordering.field =
                                                    field_name;
                                                newState.ordering.order =
                                                    "desc";
                                            } else {
                                                newState.ordering.order =
                                                    newState.ordering.order ===
                                                    "desc"
                                                        ? "asc"
                                                        : "desc";
                                            }
                                            return newState;
                                        })
                                    }
                                >
                                    {dateShown === "detection_timestamp"
                                        ? "Detected at"
                                        : "Matched at"}
                                    <FontAwesomeIcon
                                        icon={
                                            tableState.ordering.field ===
                                                "data_point__detection_timestamp" ||
                                            tableState.ordering.field ===
                                                "created_at"
                                                ? faSortFilled
                                                : faSort
                                        }
                                        className={`
                                            cursor-pointer hover:text-black
                                            ${
                                                tableState.ordering.field ===
                                                    "data_point__detection_timestamp" ||
                                                tableState.ordering.field ===
                                                    "created_at"
                                                    ? "text-black"
                                                    : "text-gray-400"
                                            }
                                        `}
                                        size="sm"
                                    />
                                </button>
                            </div>
                        </th>
                        <th className="px-2 py-0.5 w-36">
                            <div className="flex items-center justify-start gap-1">
                                Site
                            </div>
                        </th>
                        <th></th>
                    </tr>
                </thead>
                <tbody>
                    {emissions.map((emission, index) => (
                        <tr
                            key={emission.id}
                            onClick={() => goToEmission(emission)}
                            className="hover:bg-gray-50 text-ae-slate-600 cursor-pointer border-b last:border-0"
                        >
                            <td className="text-xs text-left px-1 py-0.5">
                                {index + 1}.
                            </td>
                            <td className="px-1 py-0.5">
                                <div className="text-center font-bold">
                                    <span className="text-gray-800">
                                        {emission.dataPoint.detectedRate
                                            ? (
                                                  emission.dataPoint
                                                      .detectedRate / 1000
                                              ).toFixed(0)
                                            : " - "}
                                    </span>
                                    <span className="ml-1 text-xs text-gray-500">
                                        kg/h
                                    </span>
                                </div>
                            </td>
                            <td className="px-1 py-0.5 text-right">
                                {formatDate(
                                    dateShown === "detection_timestamp"
                                        ? emission.dataPoint.detectionTimestamp
                                        : emission.createdAt,
                                )}
                            </td>
                            <td className="px-2 py-0.5 text-left max-w-36">
                                <span
                                    title={emission.site?.siteName}
                                    className="line-clamp-1"
                                >
                                    {emission.site?.siteName || (
                                        <span className="italic text-gray-400">
                                            Site not assigned
                                        </span>
                                    )}
                                </span>
                            </td>
                            <td className="px-1 py-0.5">
                                <EventStatusV2Display
                                    eventStatus={emission.mainEvent.eventStatus}
                                    hideText
                                />
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <InsightsContainer>
            <div className="flex items-center justify-between mb-1">
                <p className="font-bold text-sm ml-1">Emissions</p>
                <div className="flex text-xs items-center">
                    <button
                        onClick={() =>
                            setEmissionType((e) =>
                                e === "THIRD_PARTY"
                                    ? "SELF_REPORTED"
                                    : "THIRD_PARTY",
                            )
                        }
                        className="mr-2 flex items-center gap-1 text-ae-blue-550 text-xs hover:bg-ae-blue-50 p-1 rounded"
                    >
                        <FontAwesomeIcon icon={faSatellite} />
                        {emissionType === "THIRD_PARTY"
                            ? "Third Party"
                            : "Company Monitoring"}
                    </button>
                    <button
                        disabled={tableState.page === 1}
                        onClick={() =>
                            setTableState((ts) => ({
                                ...ts,
                                page: ts.page - 1,
                            }))
                        }
                        className={`
                            flex items-center gap-1
                            text-xs p-1 rounded
                            ${tableState.page === 1 ? "text-gray-500" : "text-ae-blue-550  hover:bg-ae-blue-50"}
                        `}
                    >
                        <FontAwesomeIcon icon={faChevronsLeft} />
                    </button>
                    <span className="text-ae-slate-600">
                        {tableState.page} / {emissionsQuery.data?.numPages}
                    </span>
                    <button
                        disabled={
                            emissionsQuery.data?.numPages < tableState.page
                        }
                        onClick={() =>
                            setTableState((ts) => ({
                                ...ts,
                                page: ts.page + 1,
                            }))
                        }
                        className={`
                            flex items-center gap-1
                            text-xs p-1 rounded
                            ${emissionsQuery.data?.numPages < tableState.page ? "text-gray-500" : "text-ae-blue-550  hover:bg-ae-blue-50"}
                        `}
                    >
                        <FontAwesomeIcon icon={faChevronsRight} />
                    </button>
                    <div className="ml-2">
                        <SettingsDropdownProvider>
                            <div className="px-1">
                                <div className="text-xs text-ae-slate-600">
                                    Emission type:
                                </div>
                                <label className="flex items-center text-sm space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="emissionType"
                                        checked={emissionType === "THIRD_PARTY"}
                                        onChange={() =>
                                            setEmissionType("THIRD_PARTY")
                                        }
                                        className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">
                                        3rd Party
                                    </span>
                                </label>
                                <label className="flex items-center text-sm space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="emissionType"
                                        checked={
                                            emissionType === "SELF_REPORTED"
                                        }
                                        onChange={() =>
                                            setEmissionType("SELF_REPORTED")
                                        }
                                        className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">
                                        Company Monitoring
                                    </span>
                                </label>
                                <hr className="my-2" />
                                <div className="text-xs text-ae-slate-600">
                                    Display:
                                </div>
                                <label className="flex items-center text-sm space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="modeState"
                                        checked={
                                            dateShown === "detection_timestamp"
                                        }
                                        onChange={() => {
                                            setDateShown("detection_timestamp");
                                            setTableState((ts) => ({
                                                ...ts,
                                                ordering: {
                                                    field: "data_point__detection_timestamp",
                                                    order: "desc",
                                                },
                                            }));
                                        }}
                                        className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">
                                        Detection Timestamp
                                    </span>
                                </label>
                                <label className="flex items-center text-sm space-x-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        name="modeState"
                                        checked={
                                            dateShown === "match_timestamp"
                                        }
                                        onChange={() => {
                                            setDateShown("match_timestamp");
                                            setTableState((ts) => ({
                                                ...ts,
                                                ordering: {
                                                    field: "created_at",
                                                    order: "desc",
                                                },
                                            }));
                                        }}
                                        className="w-3 h-3 text-blue-600 bg-gray-100 border-gray-300 focus:ring-blue-500"
                                    />
                                    <span className="text-gray-700">
                                        Match Timestamp
                                    </span>
                                </label>
                            </div>
                        </SettingsDropdownProvider>
                    </div>
                </div>
            </div>
            <div className="relative">
                {emissionsQuery.isFetching && <LoadingOverlay />}
                {renderEmissions(emissionsQuery.data?.results)}
            </div>
        </InsightsContainer>
    );
};

export const InsightsData = () => {
    const [insightsConfig] = useAtom(insightsConfigAtom);

    return (
        <div className="flex flex-col gap-1 w-[400px]">
            {insightsConfig.generalCounts && <GeneralCounts />}
            {insightsConfig.emissionList && <EmissionList />}
            {insightsConfig.infraInsights && <InfrastuctureInsightsRow />}
            {insightsConfig.thirdPartyInsights && (
                <ThirdPartyEmissionInsights />
            )}
            {insightsConfig.nonDetects && <NonDetectInsights />}
            {insightsConfig.bridgerInsights && <BridgerEmissionInsights />}
        </div>
    );
};
